import { useState } from "react";
import { createUserWithEmailAndPassword, deleteUser, sendPasswordResetEmail, signInWithEmailAndPassword } from "firebase/auth";
import { doc, getDoc, serverTimestamp, writeBatch } from "firebase/firestore";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { DEFAULT_PLAN_ID, getPlanDefinition } from "../plans";
import { fallbackUsernameFromEmail, normalizeUsername } from "../utils/identity";

const defaultWorkspaceName = (email) => {
  const name = email.split("@")[0]?.trim();
  return name ? `${name}'s Workspace` : "My Workspace";
};

const defaultPlan = getPlanDefinition(DEFAULT_PLAN_ID);

export default function Login() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  const destination = location.state?.from || "/dashboard";

  const handleLogin = async () => {
    setLoading(true);
    setError("");
    setMessage("");

    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      navigate(destination, { replace: true });
    } catch (loginError) {
      setError(loginError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    setLoading(true);
    setError("");
    setMessage("");

    try {
      const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
      const workspaceName = defaultWorkspaceName(email.trim());
      const normalizedUsername = normalizeUsername(username) || fallbackUsernameFromEmail(email.trim());
      const usernameSnapshot = await getDoc(doc(db, "usernames", normalizedUsername));

      if (usernameSnapshot.exists()) {
        await deleteUser(credential.user);
        throw new Error("That username is already taken. Try another one.");
      }

      const batch = writeBatch(db);

      batch.set(doc(db, "users", credential.user.uid), {
        email: credential.user.email,
        username: normalizedUsername,
        workspaceId: credential.user.uid,
        workspaceName,
        workspaceRole: "owner",
        plan: defaultPlan.id,
        planName: defaultPlan.name,
        seatLimit: defaultPlan.userLimit,
        programLimit: defaultPlan.programLimit,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });

      batch.set(doc(db, "workspaces", credential.user.uid), {
        name: workspaceName,
        ownerId: credential.user.uid,
        ownerUsername: normalizedUsername,
        plan: defaultPlan.id,
        planName: defaultPlan.name,
        seatLimit: defaultPlan.userLimit,
        programLimit: defaultPlan.programLimit,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });

      batch.set(doc(db, "workspaces", credential.user.uid, "members", credential.user.uid), {
        uid: credential.user.uid,
        email: credential.user.email,
        username: normalizedUsername,
        role: "owner",
        status: "active",
        joinedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });

      batch.set(doc(db, "usernames", normalizedUsername), {
        uid: credential.user.uid,
        username: normalizedUsername,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: false });

      await batch.commit();

      navigate("/dashboard", { replace: true });
    } catch (registerError) {
      setError(registerError.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!email.trim()) {
      setError("Enter your email first so we know where to send the reset link.");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      await sendPasswordResetEmail(auth, email.trim());
      setMessage("Password reset email sent.");
    } catch (resetError) {
      setError(resetError.message);
    } finally {
      setLoading(false);
    }
  };

  const isRegister = mode === "register";

  return (
    <div className="container auth-shell">
      <div className="hero-card">
        <span className="eyebrow">Account Access</span>
        <h1>{isRegister ? "Create your creator revenue workspace" : "Sign in to your revenue dashboard"}</h1>
        <p>
          Start with the Basic plan, track up to {defaultPlan.programLimit} content links, and see which platforms and
          posts are turning attention into income.
        </p>
      </div>

      <div className="card auth-card">
        <div className="mode-toggle">
          <button
            className={isRegister ? "ghost-button" : "active-tab"}
            onClick={() => setMode("login")}
            type="button"
          >
            Login
          </button>
          <button
            className={isRegister ? "active-tab" : "ghost-button"}
            onClick={() => setMode("register")}
            type="button"
          >
            Register
          </button>
        </div>

        <label className="field-label" htmlFor="email">
          Email
        </label>
        <input
          id="email"
          placeholder="you@company.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />

        {isRegister ? (
          <>
            <label className="field-label" htmlFor="username">
              Username
            </label>
            <input
              id="username"
              placeholder="yourhandle"
              value={username}
              onChange={(event) => setUsername(normalizeUsername(event.target.value))}
            />
            <p className="muted-text">This will appear publicly in your workspace as @{username || "yourhandle"}.</p>
          </>
        ) : null}

        <label className="field-label" htmlFor="password">
          Password
        </label>
        <input
          id="password"
          placeholder="At least 6 characters"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />

        {error ? <p className="status error-text">{error}</p> : null}
        {message ? <p className="status success-text">{message}</p> : null}

        <button disabled={loading} onClick={isRegister ? handleRegister : handleLogin} type="button">
          {loading ? "Working..." : isRegister ? "Create Account" : "Login"}
        </button>

        {!isRegister ? (
          <button className="ghost-button" disabled={loading} onClick={handlePasswordReset} type="button">
            Reset Password
          </button>
        ) : null}
      </div>
    </div>
  );
}
